import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as referralApi from '@/api/referrals';

/**
 * Query keys for referral-related queries
 */
export const referralKeys = {
  all: ['referrals'] as const,
  stats: (userId: string) => [...referralKeys.all, 'stats', userId] as const,
  isReferred: (userId: string) => [...referralKeys.all, 'isReferred', userId] as const,
  whitelistStatus: (userId: string) => [...referralKeys.all, 'whitelistStatus', userId] as const,
};

/**
 * Hook to get referral stats for a user
 * Refetches every 30 seconds to keep referral code up to date
 */
export const useReferralStats = (userId: string | undefined) => {
  return useQuery({
    queryKey: referralKeys.stats(userId || ''),
    queryFn: () => referralApi.getReferralStats(userId!),
    enabled: !!userId,
    staleTime: 25000, // Consider data stale after 25 seconds
  });
};


/**
 * Hook to check if user is referred
 * This rarely changes, so no auto-refetch
 */
export const useIsUserReferred = (userId: string | undefined) => {
  return useQuery({
    queryKey: referralKeys.isReferred(userId || ''),
    queryFn: () => referralApi.isUserReferred(userId!),
    enabled: !!userId,
    staleTime: Number.POSITIVE_INFINITY, // Never consider stale
  });
};

/**
 * Hook to get whitelist status for a user
 * Refetches every 45 seconds
 */
export const useWhitelistStatus = (userId: string | undefined) => {
  return useQuery({
    queryKey: referralKeys.whitelistStatus(userId || ''),
    queryFn: () => referralApi.getWhitelistStatus(userId!),
    enabled: !!userId,
    staleTime: 40000, // Consider data stale after 40 seconds
  });
};


interface JoinWithReferralParams {
  telegramId: string;
  referralCode?: string;
}

/**
 * Hook to join with optional referral code
 */
export const useJoinWithReferral = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: JoinWithReferralParams) =>
      referralApi.joinWithReferral(
        params.telegramId,
        params.referralCode
      ),
      onSuccess: (_, params) => {
        queryClient.invalidateQueries({ queryKey: referralKeys.whitelistStatus(params.telegramId) });
      },
  });
};


